// src/components/inputs/InstitutionsMultiSelect.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { HiOutlineChevronDown, HiCheck } from "react-icons/hi";

function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (event) => {
      const el = ref.current;

      // 1) Clique dentro do botão/trigger → ignora
      if (el && el.contains(event.target)) return;

      // 2) Clique dentro do dropdown (portal) → ignora
      if (event.target.closest?.('[data-inst-multi-root="true"]')) return;

      // 3) Qualquer outra área → realmente é "outside"
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

function DropdownPortal({ open, anchorRect, children }) {
  if (!open || !anchorRect) return null;

  const viewportW = window.innerWidth || document.documentElement.clientWidth;
  const viewportH = window.innerHeight || document.documentElement.clientHeight;

  const width = Math.min(360, Math.max(220, anchorRect.width));
  const margin = 8;
  const offset = 6;

  const left = Math.min(
    Math.max(margin, anchorRect.left),
    Math.max(margin, viewportW - width - margin)
  );

  const spaceBelow = viewportH - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const preferredMaxHeight = 260;
  let maxHeight;
  let top;

  // abre pra cima ou pra baixo baseado no espaço de tela
  if (spaceBelow < 160 && spaceAbove > spaceBelow) {
    maxHeight = Math.min(preferredMaxHeight, spaceAbove);
    top = Math.max(margin, anchorRect.bottom - maxHeight - offset);
  } else {
    maxHeight = Math.min(preferredMaxHeight, spaceBelow);
    top = Math.min(anchorRect.bottom + offset, viewportH - margin - maxHeight);
  }

  return ReactDOM.createPortal(
    <div
      data-inst-multi-root="true"
      style={{
        position: "fixed",
        top,
        left,
        width,
        maxHeight,
        zIndex: 99999,
      }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden flex flex-col"
    >
      <div className="flex-1 overflow-y-auto soft-scroll">{children}</div>
    </div>,
    document.body
  );
}

export default function InstitutionsMultiSelect({
  options = [], // [{ id, name, logoUrl, ... }]
  value = [], // ['1','2','3']  (IDs em string)
  onChange, // (nextIds: string[]) => void
  disabled = false,
  label,
  placeholder = "Selecione instituições",
}) {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [search, setSearch] = useState("");

  const selectedIds = (value || []).map(String);

  const selectedOptions = options.filter((o) =>
    selectedIds.includes(String(o.id))
  );

  const handleOpen = () => {
    if (disabled) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setAnchorRect(rect);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  useClickOutside(triggerRef, () => {
    if (open) setOpen(false);
  });

  const toggleId = (id) => {
    if (disabled) return;
    const sId = String(id);
    let next;
    if (selectedIds.includes(sId)) {
      next = selectedIds.filter((x) => x !== sId);
    } else {
      next = [...selectedIds, sId];
    }

    // 👉 aqui disparamos pro pai (TaskModal.handleChangeInstitutions)
    onChange?.(next);
  };

  const filteredOptions = options.filter((o) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const name = (o.name || o.label || "").toLowerCase();
    const code = (o.code || o.shortName || "").toLowerCase();
    return name.includes(term) || code.includes(term);
  });

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={`w-full flex items-center justify-between rounded-md border px-2 py-1.5 text-left text-xs ${
          disabled
            ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
        }`}
      >
        <div className="flex flex-wrap gap-1">
          {selectedOptions.length === 0 && (
            <span className="text-gray-400 truncate">{placeholder}</span>
          )}
          {selectedOptions.slice(0, 3).map((opt) => (
            <span
              key={opt.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
            >
              {opt.logoUrl && (
                <img
                  src={opt.logoUrl}
                  alt={opt.name}
                  className="w-3.5 h-3.5 rounded-sm object-contain"
                />
              )}
              <span className="max-w-[90px] truncate text-[11px]">
                {opt.name || opt.label || `Banco ${opt.id}`}
              </span>
            </span>
          ))}
          {selectedOptions.length > 3 && (
            <span className="text-[11px] text-gray-500">
              +{selectedOptions.length - 3}
            </span>
          )}
        </div>
        <HiOutlineChevronDown
          className={`ml-2 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown em portal */}
      <DropdownPortal open={open} anchorRect={anchorRect}>
        <div className="border-b px-2 py-1.5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar banco..."
            className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300"
          />
        </div>

        <div className="py-1">
          {filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">
              Nenhuma instituição encontrada.
            </div>
          )}
          {filteredOptions.map((opt) => {
            const idStr = String(opt.id);
            const isSelected = selectedIds.includes(idStr);

            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggleId(opt.id)} // 👉 cada clique dispara onChange(nextIds)
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left ${
                  isSelected
                    ? "bg-gray-100 text-gray-900"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <span className="inline-flex items-center justify-center w-4 h-4 rounded border border-gray-300 bg-white">
                  {isSelected && <HiCheck className="w-3 h-3 text-gray-700" />}
                </span>
                {opt.logoUrl && (
                  <img
                    src={opt.logoUrl}
                    alt={opt.name}
                    className="w-4 h-4 rounded-sm object-contain"
                  />
                )}
                <span className="flex-1 truncate">
                  {opt.name || opt.label || `Banco ${opt.id}`}
                </span>
                {opt.code && (
                  <span className="text-[10px] text-gray-400">{opt.code}</span>
                )}
              </button>
            );
          })}
        </div>
      </DropdownPortal>
    </div>
  );
}
